/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import { useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import Button, { LinkButton } from '@atlaskit/button/new';
import { IconTile } from '@atlaskit/icon';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import VideoIcon from '@atlaskit/icon/core/video';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Flex, Grid, Inline, Text, xcss } from '@atlaskit/primitives';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { EventDate } from '../../../../../../../common/ui/event-date';
import {
	MentionedByLinkedResults,
	SimilarLinkedResults,
} from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { eventTimeRange } from '../../../../../../../common/utils/event-time';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type {
	searchGraphCalendar_AtlassianSearchPage$data,
	searchGraphCalendar_AtlassianSearchPage$key,
} from './__generated__/searchGraphCalendar_AtlassianSearchPage.graphql';
import { messages } from './messages';

const RELATED_EVENTS_STATE = {
	UPCOMING: 'upcoming',
	PAST: 'past',
	CLOSED: 'closed',
} as const;
type RelatedEventsState = (typeof RELATED_EVENTS_STATE)[keyof typeof RELATED_EVENTS_STATE];

type CalendarEventMeetingProps = {
	fromDate?: string | null;
	toDate?: string | null;
	meetingLink?: string | null;
};

export const CalendarEventMeetingLink = ({
	fromDate,
	toDate,
	meetingLink,
}: CalendarEventMeetingProps) => {
	const { formatMessage } = useIntl();

	if (!fromDate || !meetingLink || !toDate) {
		return null;
	}

	const currentDate = new Date();
	const startDate = new Date(fromDate);
	startDate.setHours(startDate.getHours() - 2); // Checks if the event is within 2 hours
	const endDate = new Date(toDate);
	const eventIsWithin2hoursOrIsOngoing = startDate < currentDate && currentDate < endDate;

	return (
		<LinkButton
			iconBefore={() => (
				<VideoIcon
					color="var(--ds-icon-subtle)"
					label={''} // Presentation Only
				/>
			)}
			href={meetingLink}
		>
			{eventIsWithin2hoursOrIsOngoing
				? formatMessage(messages.joinMeetingLink)
				: formatMessage(messages.meetingLink)}
		</LinkButton>
	);
};

const calendarEventLinkStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',
	':hover': {
		color: 'color.link',
		textDecoration: 'underline',
	},
	':active': {
		textDecoration: 'none',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
	display: 'flex',
});

const relatedCalendarEventMetadataStyles = xcss({
	gridColumn: 2,
	alignItems: 'center',
});

const nameContainer = xcss({
	whiteSpace: 'nowrap',
});

type LinkedEntities = Exclude<
	searchGraphCalendar_AtlassianSearchPage$data['linkedEntities'],
	null | undefined
>;
type LinkedEntity = LinkedEntities[number];

type RelatedCalendarEvents = Required<Pick<LinkedEntity, 'id' | 'entity' | 'title' | 'url'>>;

const RelatedCalendarEvents = ({ calendarEvents }: { calendarEvents: RelatedCalendarEvents[] }) => {
	const { formatMessage } = useIntl();

	return (
		<Grid gap="space.200">
			{calendarEvents.map((calendarEvent) => {
				const { id, entity, title, url } = calendarEvent;

				if (!entity) {
					return null;
				}

				const labelMessageComponent = (
					<Text size="small">{formatMessage(messages.eventLabel)}</Text>
				);

				const eventDateComponent = (
					<Text size="small">
						<EventDate date={entity.eventStartTime} />
					</Text>
				);

				const eventTimeComponent =
					entity.eventStartTime && entity.eventEndTime ? (
						<Text size="small">
							{eventTimeRange({
								from: entity.eventStartTime,
								to: entity.eventEndTime,
							})}
						</Text>
					) : null;

				const meetingLink = entity.videoMeetingUrl ? (
					<Text size="small">
						<Anchor xcss={calendarEventLinkStyles} href={entity.videoMeetingUrl}>
							{formatMessage(messages.meetingLink)}
						</Anchor>
					</Text>
				) : null;

				return (
					<Grid
						key={id}
						templateColumns="min-content 1fr"
						alignItems="center"
						columnGap="space.100"
					>
						<IconTile
							size="16"
							icon={CalendarIcon}
							label={formatMessage(messages.recurringEventIcon)}
							appearance="blue"
						/>
						<Text size="small" weight="semibold">
							<Anchor xcss={calendarEventLinkStyles} href={url}>
								{title}
							</Anchor>
						</Text>

						<Flex xcss={relatedCalendarEventMetadataStyles}>
							<SearchMetadata
								parts={[
									['calendar-label', labelMessageComponent],
									['event-date', eventDateComponent],
									['event-time', eventTimeComponent],
									['meeting-link', meetingLink],
								]}
							/>
						</Flex>
					</Grid>
				);
			})}
		</Grid>
	);
};

const calendarEventActionStyles = xcss({
	display: 'flex',
	gap: 'space.100',
});

type CalendarEventActionProps = CalendarEventMeetingProps & {
	linkedEntities: Exclude<
		searchGraphCalendar_AtlassianSearchPage$data['linkedEntities'],
		null | undefined
	>;
};
const CalendarEventActions = ({
	fromDate,
	toDate,
	meetingLink,
	linkedEntities,
}: CalendarEventActionProps) => {
	const { formatMessage } = useIntl();
	const [relatedEventsState, setEventsToDisplay] = useState<RelatedEventsState>(
		RELATED_EVENTS_STATE.CLOSED,
	);

	const isUpcomingEventsSelected = relatedEventsState === RELATED_EVENTS_STATE.UPCOMING;
	const isPastEventsSelected = relatedEventsState === RELATED_EVENTS_STATE.PAST;

	const data = useMemo(() => {
		const currentDate = new Date();

		const linkedCalendarEvents = linkedEntities.flatMap((linkedEntity) =>
			linkedEntity.entity
				? [
						{
							id: linkedEntity.id,
							title: linkedEntity.title,
							url: linkedEntity.url,
							entity: linkedEntity.entity,
						},
					]
				: [],
		);

		const [pastEvents, upcomingEvents] = linkedCalendarEvents.reduce<
			[RelatedCalendarEvents[], RelatedCalendarEvents[]]
		>(
			(events, currentEvent) => {
				const isPastEvent =
					currentEvent.entity.eventStartTime &&
					new Date(currentEvent.entity.eventStartTime) < currentDate;

				if (isPastEvent) {
					return [events[0].concat(currentEvent), events[1]];
				}
				return [events[0], events[1].concat(currentEvent)];
			},
			[[], []],
		);

		return {
			pastEvents,
			upcomingEvents,
		};
	}, [linkedEntities]);

	return (
		<Grid gap="space.200">
			<Box xcss={calendarEventActionStyles}>
				<CalendarEventMeetingLink fromDate={fromDate} toDate={toDate} meetingLink={meetingLink} />

				{!!data.upcomingEvents.length && (
					<Button
						iconAfter={(iconProps) =>
							isUpcomingEventsSelected ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						onClick={() =>
							setEventsToDisplay(
								isUpcomingEventsSelected
									? RELATED_EVENTS_STATE.CLOSED
									: RELATED_EVENTS_STATE.UPCOMING,
							)
						}
						isSelected={isUpcomingEventsSelected}
					>
						{formatMessage(messages.upcomingEventsButton)}
					</Button>
				)}

				{!!data.pastEvents.length && (
					<Button
						iconAfter={(iconProps) =>
							isPastEventsSelected ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						onClick={() =>
							setEventsToDisplay(
								isPastEventsSelected ? RELATED_EVENTS_STATE.CLOSED : RELATED_EVENTS_STATE.PAST,
							)
						}
						isSelected={isPastEventsSelected}
					>
						{formatMessage(messages.pastEventsButton)}
					</Button>
				)}
			</Box>

			{relatedEventsState === RELATED_EVENTS_STATE.UPCOMING && (
				<RelatedCalendarEvents calendarEvents={data.upcomingEvents} />
			)}
			{relatedEventsState === RELATED_EVENTS_STATE.PAST && (
				<RelatedCalendarEvents calendarEvents={data.pastEvents} />
			)}
		</Grid>
	);
};

export const SearchGraphCalendar = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	is3pGroupingEnabled,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphCalendar_AtlassianSearchPage$key>) => {
	const data = useFragment(
		graphql`
			fragment searchGraphCalendar_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				url
				description
				excerpt
				permissionLevel
				integrationId
				entity @optIn(to: "ExternalDataDepotQueryV2") {
					... on ExternalCalendarEvent {
						createdBy {
							thirdPartyUser {
								id

								name
							}
						}
						eventEndTime
						eventStartTime
						recurringEventId
						videoMeetingUrl
					}
				}
				linkedEntities {
					id
					title
					url
					entity {
						... on ExternalCalendarEvent {
							eventStartTime
							eventEndTime
							videoMeetingUrl
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { url, description, permissionLevel, entity, title, linkedEntities, excerpt, integrationId } =
		data;

	const calendarEvent = useMemo(() => {
		const eventTime =
			entity?.eventStartTime && entity?.eventEndTime
				? eventTimeRange({ from: entity?.eventStartTime, to: entity?.eventEndTime })
				: null;

		const recurringEvent = entity?.recurringEventId
			? formatMessage(messages.recurringMetadataLabel)
			: null;

		const getCreator = () => {
			const fullName = entity?.createdBy?.thirdPartyUser?.name || '';
			const firstName = fullName?.split(' ')?.[0] || formatMessage(messages.externalPerson);

			const actionLabel = formatMessage(messages.created, { person: firstName });

			return (
				<Inline space="space.025" alignBlock="center">
					<ProfileAvatar name={fullName} />
					<Box xcss={nameContainer}>{actionLabel}</Box>
				</Inline>
			);
		};

		return {
			labelMessage: formatMessage(messages.eventLabel),
			eventTime,
			recurringEvent,
			createdBy: getCreator(),
		};
	}, [entity, formatMessage]);

	const eventDateComponent = <EventDate date={entity?.eventStartTime} />;

	const body = <Box paddingBlockEnd="space.100">{description}</Box>;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const headerMetadataParts = [
		calendarEvent.labelMessage,
		['event-date', eventDateComponent],
		['event-time', calendarEvent.eventTime],
		calendarEvent.recurringEvent,
		['padlock', padlockIcon],
		['createdBy', calendarEvent.createdBy],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const isGoogleCalendar = integrationId === 'ari:cloud:platform::integration/google';
	const shouldRenderSimilarResults =
		isGoogleCalendar &&
		linkedResults?.some((linkedResult) => linkedResult.category?.toUpperCase() === 'SIMILAR');
	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	const footerComponent = shouldShowRelatedLinks ? (
		shouldRenderSimilarResults ? (
			<SimilarLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				position={position}
				onResultClicked={onResultClicked}
				isAllProductSelected={!selectedProduct}
				onResultContextMenu={onResultContextMenu}
				is3pGroupingEnabled={is3pGroupingEnabled}
				type={calendarEvent.labelMessage}
			/>
		) : (
			<MentionedByLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				position={position}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				isAllProductSelected={!selectedProduct}
			/>
		)
	) : (
		<CalendarEventActions
			meetingLink={entity?.videoMeetingUrl}
			fromDate={entity?.eventStartTime}
			toDate={entity?.eventEndTime}
			linkedEntities={linkedEntities ?? []}
		/>
	);

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={excerpt ? processExcerpt(excerpt, true) : body} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
